/**
 * Portal guard — the two portals (internal staff + client) share ONE Supabase
 * auth backend and session, so valid credentials authenticate on either login
 * screen. Which portal an account *belongs* to is decided by its role. These
 * helpers let each login screen detect a wrong-portal sign-in and steer the
 * user to the right place — WITHOUT signing the shared session out (that would
 * kill a legitimate session the user holds for the other portal).
 */
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type AppRole = Database["public"]["Enums"]["app_role"];

/** Which portal an account belongs to, derived from its role. */
export type PortalAudience = "client" | "staff";

/** A role of `client` → client portal; everything else (incl. no role yet) → staff. */
export const audienceForRole = (role: AppRole | null): PortalAudience =>
  role === "client" ? "client" : "staff";

/**
 * Thrown by a login handler when the account belongs to the OTHER portal.
 * `accountAudience` is where the account actually belongs (so the UI can point
 * the user there).
 */
export class WrongPortalError extends Error {
  readonly accountAudience: PortalAudience;
  constructor(accountAudience: PortalAudience) {
    super(
      accountAudience === "client"
        ? "This is a client account."
        : "This is a staff account.",
    );
    this.name = "WrongPortalError";
    this.accountAudience = accountAudience;
  }
}

/** Resolve a user's role via the secure RPC. Returns null on error / no role. */
export const fetchUserRole = async (userId: string): Promise<AppRole | null> => {
  const { data, error } = await supabase.rpc("get_user_role", { _user_id: userId });
  if (error) {
    console.error("portalGuard: get_user_role failed", error);
    return null;
  }
  return (data as AppRole) ?? null;
};
